import { useState, useEffect, useRef } from "react";
import { casualData } from "../api/casualData.js";
import { useRealtime } from "./realtime/RealtimeProvider.jsx";
import { usePresence } from "./realtime/usePresence.js";
import { colorForClientId } from "./realtime/presenceColor.js";
import { RowSuppliersModal } from "./RowSuppliersModal.jsx";
import { KainininkasLinkModal } from "./KainininkasLinkModal.jsx";
import { ROOM_TYPES, TOILET_FIXTURE_TYPES, BOILER_FIXTURE_TYPES, KITCHEN_FIXTURE_TYPES } from "./SkaiciuokleView.jsx";

export const COLUMN_TYPES = [
  { id: "number", label: "Skaičius" },
  { id: "text", label: "Tekstas" },
  { id: "characteristic", label: "Charakteristika" },
  { id: "category", label: "Kategorija" },
  { id: "dataMatrixTable", label: "Duomenų matricos lentelė" },
  { id: "checkbox", label: "Varnelė" },
];
const COLUMN_TYPE_LABELS = Object.fromEntries(COLUMN_TYPES.map(ct => [ct.id, ct.label]));

// "Kategorija" stulpelis — pirmiausia pasirenkamas konkretus tipas, o langelio
// dropdown'e rodomas TO tipo pozicijų sąrašas (tas pats, kas naudojamas Skaičiuoklėje).
const CATEGORY_OPTIONS = ["Prietaisai", "Patalpos"];
const CATEGORY_ITEM_LISTS = {
  "Prietaisai": [...new Set([...TOILET_FIXTURE_TYPES, ...BOILER_FIXTURE_TYPES, ...KITCHEN_FIXTURE_TYPES])],
  "Patalpos": ROOM_TYPES,
};

// "Charakteristika" stulpelis — ta pati logika: pasirenkama konkreti charakteristika,
// o langelio dropdown'e rodomos JOS reikšmės.
const CHARACTERISTIC_TYPES = {
  "Diametras": ["d16", "d18", "d20", "d25", "d32", "d40", "d50", "d75", "d100", "d110", "d125", "d160", "d200", "d250"],
};
const CHARACTERISTIC_TYPE_NAMES = Object.keys(CHARACTERISTIC_TYPES);

function columnSubtypeLabel(col) {
  if (col.type === "characteristic") return col.characteristicType || "nepasirinkta";
  if (col.type === "category") return col.categoryType || "nepasirinktas tipas";
  return null;
}

// Spalvotas žiedas prie langelio/eilutės, kurią ŠIUO METU redaguoja kita
// sesija (kita master paskyra, ar ta pati per kitą naršyklės skirtuką) —
// žr. usePresence.js. Niekada nerodoma savo pačios fokusavimui.
function PresenceRing({ presence }) {
  if (!presence) return null;
  const color = colorForClientId(presence.clientId);
  return (
    <span title={`${presence.email} redaguoja`} style={{
      position: "absolute", top: -4, right: -4, width: 9, height: 9, borderRadius: "50%",
      background: color, border: "2px solid #fff", boxShadow: "0 0 0 1px " + color,
    }} />
  );
}

// Pakartotinai naudojamas lentelės redaktorius — tas pats mechanizmas naudojamas
// ir "Duomenų matrica", ir "Kainininkas" (kiekvienos grupės kainininko lentelei).
// Kiekvienas pakeitimas (stulpelis, eilutė, langelis) išsisaugo IŠKART per savo
// granuliuotą REST kvietimą — nėra atskiro "juodraščio", todėl nėra ir ką
// "atšaukti": "Redaguoti" tik perjungia tarp rodymo ir redagavimo UI.
export function EditableDataTable({ tableId, onBack, templates, showSuppliers = false, showKainininkasLink = false }) {
  const [table, setTable] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [conflictNote, setConflictNote] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  // Tiekėjai saugomi ATSKIRAI nuo `table.rows` (žr. price-tables/routes.ts
  // komentarą) — kad paprastas langelio redagavimas niekada netyčia
  // neišvalytų tos eilutės tiekėjų sąrašo.
  const [rowSuppliers, setRowSuppliers] = useState({});
  const [knownSuppliers, setKnownSuppliers] = useState([]);
  const [supplierModalRowId, setSupplierModalRowId] = useState(null);
  // "Gyvo" susiejimo su Kainininku žmogui skaitoma nuoroda — ta pati
  // seseriška struktūra kaip rowSuppliers (žr. price-tables/routes.ts).
  const [linkedSources, setLinkedSources] = useState({});
  const [linkModalRowId, setLinkModalRowId] = useState(null);
  // "Duomenų matricos lentelė" stulpelio tipas — dropdown'o pasirinkimai yra
  // VISŲ Duomenų matricos lentelių (ne Kainininko) pavadinimai, be šios pačios
  // lentelės (savireferencija neturi prasmės).
  const [dataMatrixTableNames, setDataMatrixTableNames] = useState([]);
  const savedTimeoutRef = useRef(null);
  const conflictTimeoutRef = useRef(null);
  const realtime = useRealtime();
  const { notifyFocus, notifyBlur, presenceFor } = usePresence(tableId);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    casualData.getPriceTable(tableId)
      .then(data => {
        if (cancelled) return;
        setTable(data);
        setNameDraft(data.name || "");
        setRowSuppliers(data.rowSuppliers || {});
        setLinkedSources(data.linkedSources || {});
      })
      .catch(e => console.error("Failed to load price table", e))
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [tableId]);

  useEffect(() => {
    if (!showSuppliers) return;
    let cancelled = false;
    casualData.getKnownSuppliers()
      .then(names => { if (!cancelled) setKnownSuppliers(names); })
      .catch(e => console.error("Failed to load known suppliers", e));
    return () => { cancelled = true; };
  }, [showSuppliers]);

  useEffect(() => {
    let cancelled = false;
    casualData.listDataMatrixTables()
      .then(list => {
        if (cancelled) return;
        const names = (Array.isArray(list) ? list : [])
          .filter(t => t.id !== tableId)
          .map(t => t.name || "(be pavadinimo)");
        setDataMatrixTableNames([...new Set(names)]);
      })
      .catch(e => console.error("Failed to load data matrix table list", e));
    return () => { cancelled = true; };
  }, [tableId]);

  // Gyvi pakeitimai iš kitų sesijų (kitos master paskyros ar kito skirtuko) —
  // pritaikomi vietoje, be puslapio/lentelės perkrovimo.
  useEffect(() => {
    if (!realtime) return;
    return realtime.subscribe(msg => {
      if (msg.type !== "entity") return;
      if (msg.entity === "table" && msg.id === tableId) {
        setTable(t => t && { ...t, name: msg.name ?? t.name, version: msg.version ?? t.version });
      }
      if (msg.entity === "table" && msg.action === "template_applied" && msg.tableId === tableId) {
        setTable(t => t && { ...t, version: msg.version, columns: msg.columns });
      }
      if (msg.entity === "column" && msg.tableId === tableId) {
        setTable(t => {
          if (!t) return t;
          if (msg.action === "created") return { ...t, columns: [...t.columns, msg.column] };
          if (msg.action === "updated") return { ...t, columns: t.columns.map(c => c.id === msg.column.id ? msg.column : c) };
          if (msg.action === "deleted") return { ...t, columns: t.columns.filter(c => c.id !== msg.id) };
          if (msg.action === "reordered") {
            const byId = Object.fromEntries(msg.columns.map(c => [c.id, c]));
            return { ...t, columns: t.columns.map(c => byId[c.id] || c).sort((a, b) => a.position - b.position) };
          }
          return t;
        });
      }
      if (msg.entity === "row" && msg.tableId === tableId) {
        setTable(t => {
          if (!t) return t;
          if (msg.action === "created") return { ...t, rows: [...t.rows, msg.row] };
          if (msg.action === "updated") return { ...t, rows: t.rows.map(r => r.id === msg.row.id ? msg.row : r) };
          if (msg.action === "deleted") return { ...t, rows: t.rows.filter(r => r.id !== msg.id) };
          return t;
        });
      }
      // Atskiras įvykių tipas (NE "row" viduje) — žr. price-tables/routes.ts
      // ir schema.prisma komentarus apie PriceTableRowSupplier.
      if (msg.entity === "rowSupplier" && msg.tableId === tableId) {
        setRowSuppliers(prev => {
          const list = prev[msg.rowId] || [];
          if (msg.action === "created") return { ...prev, [msg.rowId]: [...list, msg.entry] };
          if (msg.action === "updated") return { ...prev, [msg.rowId]: list.map(e => (e.id === msg.entry.id ? msg.entry : e)) };
          if (msg.action === "deleted") return { ...prev, [msg.rowId]: list.filter(e => e.id !== msg.id) };
          return prev;
        });
      }
    });
  }, [realtime, tableId]);

  function flashSaved() {
    setSavedFlash(true);
    clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => setSavedFlash(false), 2000);
  }
  function flashConflict(text) {
    setConflictNote(text);
    clearTimeout(conflictTimeoutRef.current);
    conflictTimeoutRef.current = setTimeout(() => setConflictNote(""), 4000);
  }
  async function reloadFromServer() {
    const fresh = await casualData.getPriceTable(tableId);
    setTable(fresh);
    setRowSuppliers(fresh.rowSuppliers || {});
    setLinkedSources(fresh.linkedSources || {});
  }

  if (!loaded || !table) return <p style={{ fontSize: 14, color: "#383853" }}>Kraunama...</p>;

  const templateOptions = (templates || []).filter(t => t.id !== tableId && (t.columnCount ?? 0) > 0);

  async function applyTemplate(templateTableId) {
    if (table.columns.length > 0 && !window.confirm("Pakeisti esamus stulpelius pasirinktos lentelės struktūra?")) return;
    setShowTemplatePicker(false);
    try {
      const result = await casualData.applyTemplate(tableId, templateTableId, table.version);
      setTable(t => ({ ...t, version: result.version, columns: result.columns }));
      flashSaved();
    } catch (e) {
      if (e?.status === 409) { flashConflict("Lentelė tuo pačiu metu buvo pakeista kitur — įkeliama naujausia versija."); await reloadFromServer(); }
      else console.error("Failed to apply template", e);
    }
  }

  async function commitNameIfChanged() {
    if (nameDraft === table.name) return;
    try {
      const result = await casualData.renameTable(tableId, nameDraft, table.version);
      setTable(t => ({ ...t, name: nameDraft, version: result.version }));
      flashSaved();
    } catch (e) {
      if (e?.status === 409) { flashConflict("Pavadinimą tuo pačiu metu pakeitė kitas vartotojas."); await reloadFromServer(); }
      else console.error("Failed to rename table", e);
    }
  }

  async function addColumn() {
    try {
      const column = await casualData.createColumn(tableId, { name: "", type: "text", categoryType: "", characteristicType: "" });
      setTable(t => ({ ...t, columns: [...t.columns, column] }));
    } catch (e) { console.error("Failed to add column", e); }
  }
  async function updateColumnField(col, patch) {
    setTable(t => ({ ...t, columns: t.columns.map(c => c.id === col.id ? { ...c, ...patch } : c) }));
    try {
      const updated = await casualData.updateColumn(tableId, col.id, patch, col.version);
      setTable(t => ({ ...t, columns: t.columns.map(c => c.id === col.id ? updated : c) }));
    } catch (e) {
      if (e?.status === 409) { flashConflict("Šį stulpelį tuo pačiu metu pakeitė kitas vartotojas."); setTable(t => ({ ...t, columns: t.columns.map(c => c.id === col.id ? e.body.entity : c) })); }
      else console.error("Failed to update column", e);
    }
  }
  async function removeColumn(col) {
    setTable(t => ({ ...t, columns: t.columns.filter(c => c.id !== col.id) }));
    try { await casualData.deleteColumn(tableId, col.id, col.version); }
    catch (e) { console.error("Failed to delete column", e); await reloadFromServer(); }
  }
  async function moveColumn(col, dir) {
    try {
      const result = await casualData.reorderColumn(tableId, col.id, dir < 0 ? "up" : "down", col.version);
      if (result.columns) {
        const byId = Object.fromEntries(result.columns.map(c => [c.id, c]));
        setTable(t => ({ ...t, columns: t.columns.map(c => byId[c.id] || c).sort((a, b) => a.position - b.position) }));
      }
    } catch (e) { console.error("Failed to reorder column", e); }
  }

  async function addRow() {
    try {
      const row = await casualData.createRow(tableId);
      setTable(t => ({ ...t, rows: [...t.rows, row] }));
    } catch (e) { console.error("Failed to add row", e); }
  }
  async function removeRow(row) {
    setTable(t => ({ ...t, rows: t.rows.filter(r => r.id !== row.id) }));
    try { await casualData.deleteRow(tableId, row.id, row.version); }
    catch (e) { console.error("Failed to delete row", e); await reloadFromServer(); }
  }

  function rememberKnownSupplierLocally(name) {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    setKnownSuppliers(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed].sort((a, b) => a.localeCompare(b, "lt"))));
  }
  async function addRowSupplier(rowId) {
    try {
      const entry = await casualData.createRowSupplier(tableId, rowId, { supplier: "", code: "", price: "" });
      setRowSuppliers(prev => ({ ...prev, [rowId]: [...(prev[rowId] || []), entry] }));
    } catch (e) { console.error("Failed to add row supplier", e); }
  }
  async function updateRowSupplier(rowId, entry, patch) {
    setRowSuppliers(prev => ({ ...prev, [rowId]: (prev[rowId] || []).map(e => (e.id === entry.id ? { ...e, ...patch } : e)) }));
    try {
      const updated = await casualData.updateRowSupplier(tableId, rowId, entry.id, patch, entry.version);
      setRowSuppliers(prev => ({ ...prev, [rowId]: (prev[rowId] || []).map(e => (e.id === entry.id ? updated : e)) }));
      if (patch.supplier) rememberKnownSupplierLocally(patch.supplier);
    } catch (e) {
      if (e?.status === 409) {
        flashConflict("Šį tiekėją tuo pačiu metu pakeitė kitas vartotojas.");
        setRowSuppliers(prev => ({ ...prev, [rowId]: (prev[rowId] || []).map(x => (x.id === entry.id ? e.body.entity : x)) }));
      } else console.error("Failed to update row supplier", e);
    }
  }
  async function deleteRowSupplier(rowId, entry) {
    setRowSuppliers(prev => ({ ...prev, [rowId]: (prev[rowId] || []).filter(e => e.id !== entry.id) }));
    try { await casualData.deleteRowSupplier(tableId, rowId, entry.id, entry.version); }
    catch (e) { console.error("Failed to delete row supplier", e); }
  }

  // "Gyvas" susiejimas su Kainininko produktu — žr. price-tables/routes.ts.
  // Po susiejimo/atsiejimo perkraunama visa lentelė (reloadFromServer), kad
  // gautume ir naują linkedSources santrauką (gamintojas/grupė pavadinimai),
  // ne vien pačią eilutę.
  async function linkRowTo(rowId, linkedRowId) {
    const row = table.rows.find(r => r.id === rowId);
    if (!row) return;
    setLinkModalRowId(null);
    try {
      await casualData.linkRow(tableId, rowId, linkedRowId, row.version);
      await reloadFromServer();
      flashSaved();
    } catch (e) {
      if (e?.status === 409) { flashConflict("Šią eilutę tuo pačiu metu pakeitė kitas vartotojas."); await reloadFromServer(); }
      else console.error("Failed to link row", e);
    }
  }

  function updateCellLocal(rowId, colId, value) {
    setTable(t => ({ ...t, rows: t.rows.map(r => r.id === rowId ? { ...r, cells: { ...r.cells, [colId]: value } } : r) }));
  }
  // Teksto/skaičiaus langeliai: reikšmė išsisaugo paliekant lauką (onBlur), ne
  // kiekvienu klavišo paspaudimu — kad vartotojas galėtų ramiai rašyti.
  async function commitCell(rowId, colId) {
    const row = table.rows.find(r => r.id === rowId);
    if (!row) return;
    try {
      const updated = await casualData.patchRowCells(tableId, rowId, { [colId]: row.cells[colId] ?? "" }, row.version);
      setTable(t => ({ ...t, rows: t.rows.map(r => r.id === rowId ? updated : r) }));
      flashSaved();
    } catch (e) {
      if (e?.status === 409) { flashConflict("Šią eilutę tuo pačiu metu redagavo kitas vartotojas — rodoma naujausia reikšmė."); setTable(t => ({ ...t, rows: t.rows.map(r => r.id === rowId ? e.body.entity : r) })); }
      else console.error("Failed to save cell", e);
    }
  }
  // Pasirinkimai (select) neturi "rašymo" etapo — reikšmė jau galutinė iš karto.
  async function updateCellAndSave(row, colId, value) {
    updateCellLocal(row.id, colId, value);
    try {
      const updated = await casualData.patchRowCells(tableId, row.id, { [colId]: value }, row.version);
      setTable(t => ({ ...t, rows: t.rows.map(r => r.id === row.id ? updated : r) }));
      flashSaved();
    } catch (e) {
      if (e?.status === 409) { flashConflict("Šią eilutę tuo pačiu metu redagavo kitas vartotojas — rodoma naujausia reikšmė."); setTable(t => ({ ...t, rows: t.rows.map(r => r.id === row.id ? e.body.entity : r) })); }
      else console.error("Failed to save cell", e);
    }
  }

  const current = table;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          ← Atgal
        </button>
        {isEditing ? (
          <input value={nameDraft} onChange={e => setNameDraft(e.target.value)} onBlur={commitNameIfChanged}
            style={{ fontSize: 16, fontWeight: 700, color: "#1C1C1C", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 10px", flex: 1, boxSizing: "border-box" }} />
        ) : (
          <h1 style={{ fontSize: 16, fontWeight: 700, color: "#1C1C1C", flex: 1 }}>{current.name || "(be pavadinimo)"}</h1>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isEditing && templateOptions.length > 0 && (
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowTemplatePicker(v => !v)}
                style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                📋 Naudoti šabloną
              </button>
              {showTemplatePicker && (
                <div style={{ position: "absolute", top: "110%", right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 50, minWidth: 220 }}>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Pasirinkite lentelės struktūrą</div>
                  <select defaultValue="" onChange={e => { if (e.target.value) applyTemplate(e.target.value); }}
                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 12, boxSizing: "border-box" }}>
                    <option value="" disabled>Pasirinkite...</option>
                    {templateOptions.map(t => <option key={t.id} value={t.id}>{t.name || "(be pavadinimo)"}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          <button onClick={() => setIsEditing(v => !v)}
            style={{ background: isEditing ? "#f3f4f6" : "#735DFF", color: isEditing ? "#383853" : "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {isEditing ? "✓ Baigti redaguoti" : "✏️ Redaguoti"}
          </button>
        </div>
      </div>

      {/* Stulpelių konfigūracija */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c", marginBottom: 8 }}>Stulpeliai</div>
        {current.columns.length === 0 && <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>Stulpelių dar nėra.</p>}

        {!isEditing && current.columns.map(col => (
          <div key={col.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
            <span style={{ flex: 1, fontWeight: 600, color: "#1c1c1c" }}>{col.name || "(be pavadinimo)"}</span>
            <span style={{ color: "#6b7280" }}>{COLUMN_TYPE_LABELS[col.type]}{columnSubtypeLabel(col) ? ` — ${columnSubtypeLabel(col)}` : ""}</span>
          </div>
        ))}

        {isEditing && current.columns.map((col, idx) => (
          <div key={col.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <button onClick={() => moveColumn(col, -1)} disabled={idx === 0} title="Perkelti aukštyn (kairiau lentelėje)"
                  style={{ background: "none", border: "none", color: idx === 0 ? "#e5e7eb" : "#9ca3af", cursor: idx === 0 ? "default" : "pointer", fontSize: 11, lineHeight: 1, padding: "2px 4px" }}>▲</button>
                <button onClick={() => moveColumn(col, 1)} disabled={idx === current.columns.length - 1} title="Perkelti žemyn (dešiniau lentelėje)"
                  style={{ background: "none", border: "none", color: idx === current.columns.length - 1 ? "#e5e7eb" : "#9ca3af", cursor: idx === current.columns.length - 1 ? "default" : "pointer", fontSize: 11, lineHeight: 1, padding: "2px 4px" }}>▼</button>
              </div>
              <input value={col.name} onChange={e => updateColumnField(col, { name: e.target.value })}
                placeholder="Stulpelio pavadinimas"
                style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 13, boxSizing: "border-box" }} />
              <select value={col.type} onChange={e => updateColumnField(col, { type: e.target.value })}
                style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 12 }}>
                {COLUMN_TYPES.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
              </select>
              <button onClick={() => removeColumn(col)} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 16 }}>×</button>
            </div>
            {col.type === "characteristic" && (
              <div style={{ paddingLeft: 8, marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Charakteristika</div>
                <select value={col.characteristicType || ""} onChange={e => updateColumnField(col, { characteristicType: e.target.value })}
                  style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 12 }}>
                  <option value="">Pasirinkite charakteristiką</option>
                  {CHARACTERISTIC_TYPE_NAMES.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
            )}
            {col.type === "category" && (
              <div style={{ paddingLeft: 8, marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Kategorijos tipas</div>
                <select value={col.categoryType || ""} onChange={e => updateColumnField(col, { categoryType: e.target.value })}
                  style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 12 }}>
                  <option value="">Pasirinkite tipą</option>
                  {CATEGORY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            )}
          </div>
        ))}

        {isEditing && (
          <button onClick={addColumn}
            style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 8, padding: "6px 14px", fontSize: 12, color: "#9ca3af", cursor: "pointer" }}>
            + Stulpelis
          </button>
        )}
      </div>

      {/* Duomenų lentelė */}
      {current.columns.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c" }}>Duomenys</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {conflictNote && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "#b45309" }}>⚠ {conflictNote}</div>
              )}
              {savedFlash && (
                <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", display: "flex", alignItems: "center", gap: 4 }}>
                  ✓ Išsaugota
                </div>
              )}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f0f0f2", color: "#6b7280" }}>
                  {current.columns.map(col => (
                    <th key={col.id} style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{col.name || "—"}</th>
                  ))}
                  {showSuppliers && <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>Tiekėjai</th>}
                  {showKainininkasLink && <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>🔗 Kainininkas</th>}
                  {isEditing && <th></th>}
                </tr>
              </thead>
              <tbody>
                {current.rows.length === 0 && (
                  <tr><td colSpan={current.columns.length + (showSuppliers ? 1 : 0) + (showKainininkasLink ? 1 : 0) + (isEditing ? 1 : 0)} style={{ padding: "12px 8px", textAlign: "center", color: "#9ca3af" }}>Eilučių dar nėra.</td></tr>
                )}
                {current.rows.map(row => (
                  <tr key={row.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    {current.columns.map(col => {
                      const presence = presenceFor(row.id, col.id);
                      return (
                        <td key={col.id} style={{ padding: "4px 8px", position: "relative" }}>
                          <PresenceRing presence={presence} />
                          {!isEditing && col.type !== "checkbox" && <span>{row.cells[col.id] || <span style={{ color: "#d1d5db" }}>—</span>}</span>}
                          {!isEditing && col.type === "checkbox" && (
                            <input type="checkbox" checked={!!row.cells[col.id]} disabled
                              style={{ width: 16, height: 16 }} />
                          )}

                          {isEditing && col.type === "checkbox" && (
                            <input type="checkbox" checked={!!row.cells[col.id]}
                              onChange={e => updateCellAndSave(row, col.id, e.target.checked)}
                              style={{ width: 16, height: 16, cursor: "pointer" }} />
                          )}

                          {isEditing && col.type === "number" && (
                            // type="text" (ne "number") TYČIA — nebūtinai skaitinis
                            // stulpelis visada turi vien skaičius: gyvo susiejimo su
                            // Kainininku atveju langelis gali gauti ir alfanumerinę
                            // reikšmę (pvz. prekės kodą "0.2180OP"), o native <input
                            // type="number"> tokią reikšmę tyliai rodo TUŠČIĄ, nors
                            // `cells` duomenys yra teisingi — vartotojui atrodytų,
                            // kad reikšmė "nepersikėlė".
                            <input type="text" inputMode="decimal" value={row.cells[col.id] ?? ""} onChange={e => updateCellLocal(row.id, col.id, e.target.value)}
                              onFocus={() => notifyFocus(row.id, col.id)} onBlur={() => { notifyBlur(row.id, col.id); commitCell(row.id, col.id); }}
                              style={{ width: 90, border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 12, boxSizing: "border-box" }} />
                          )}
                          {isEditing && col.type === "text" && (
                            <input type="text" value={row.cells[col.id] ?? ""} onChange={e => updateCellLocal(row.id, col.id, e.target.value)}
                              onFocus={() => notifyFocus(row.id, col.id)} onBlur={() => { notifyBlur(row.id, col.id); commitCell(row.id, col.id); }}
                              style={{ width: 140, border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 12, boxSizing: "border-box" }} />
                          )}
                          {isEditing && col.type === "characteristic" && (
                            col.characteristicType ? (
                              <select value={row.cells[col.id] ?? ""} onFocus={() => notifyFocus(row.id, col.id)} onBlur={() => notifyBlur(row.id, col.id)}
                                onChange={e => updateCellAndSave(row, col.id, e.target.value)}
                                style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 12 }}>
                                <option value="">—</option>
                                {(CHARACTERISTIC_TYPES[col.characteristicType] || []).map(v => (
                                  <option key={v} value={v}>{v}</option>
                                ))}
                              </select>
                            ) : (
                              <span style={{ fontSize: 11, color: "#d1d5db" }}>Nepasirinkta charakteristika</span>
                            )
                          )}
                          {isEditing && col.type === "category" && (
                            col.categoryType ? (
                              <select value={row.cells[col.id] ?? ""} onFocus={() => notifyFocus(row.id, col.id)} onBlur={() => notifyBlur(row.id, col.id)}
                                onChange={e => updateCellAndSave(row, col.id, e.target.value)}
                                style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 12 }}>
                                <option value="">—</option>
                                {(CATEGORY_ITEM_LISTS[col.categoryType] || []).map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            ) : (
                              <span style={{ fontSize: 11, color: "#d1d5db" }}>Nepasirinktas tipas</span>
                            )
                          )}
                          {isEditing && col.type === "dataMatrixTable" && (
                            <select value={row.cells[col.id] ?? ""} onFocus={() => notifyFocus(row.id, col.id)} onBlur={() => notifyBlur(row.id, col.id)}
                              onChange={e => updateCellAndSave(row, col.id, e.target.value)}
                              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 12 }}>
                              <option value="">—</option>
                              {dataMatrixTableNames.map(name => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                      );
                    })}
                    {showSuppliers && (
                      <td style={{ padding: "4px 8px" }}>
                        {(() => {
                          const entries = rowSuppliers[row.id] || [];
                          const summary = entries.length === 0
                            ? "—"
                            : entries.map(e => e.supplier || "(be pavadinimo)").join(", ");
                          return isEditing ? (
                            <button onClick={() => setSupplierModalRowId(row.id)}
                              style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                              {entries.length > 0 ? `${entries.length} tiek. ✎` : "+ Tiekėjas"}
                            </button>
                          ) : (
                            <span style={{ fontSize: 12, color: entries.length === 0 ? "#d1d5db" : "#383853" }}>{summary}</span>
                          );
                        })()}
                      </td>
                    )}
                    {showKainininkasLink && (
                      <td style={{ padding: "4px 8px" }}>
                        {(() => {
                          const source = row.linkedRowId ? linkedSources[row.linkedRowId] : null;
                          const label = source ? `${source.manufacturerName} / ${source.groupName}` : "—";
                          return isEditing ? (
                            <button onClick={() => setLinkModalRowId(row.id)}
                              title={source ? source.tableName : undefined}
                              style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                              {source ? `${label} ✎` : "+ Susieti"}
                            </button>
                          ) : (
                            <span title={source ? source.tableName : undefined} style={{ fontSize: 12, color: source ? "#383853" : "#d1d5db" }}>{label}</span>
                          );
                        })()}
                      </td>
                    )}
                    {isEditing && (
                      <td style={{ padding: "4px 8px", textAlign: "center" }}>
                        <button onClick={() => removeRow(row)} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 15 }}>×</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isEditing && (
            <button onClick={addRow}
              style={{ marginTop: 8, background: "none", border: "1px dashed #d1d5db", borderRadius: 8, padding: "6px 14px", fontSize: 12, color: "#9ca3af", cursor: "pointer" }}>
              + Eilutė
            </button>
          )}
        </div>
      )}

      {showSuppliers && supplierModalRowId && (() => {
        const modalRow = current.rows.find(r => r.id === supplierModalRowId);
        if (!modalRow) return null;
        const rowLabel = current.columns[0] ? modalRow.cells[current.columns[0].id] : "";
        return (
          <RowSuppliersModal
            rowLabel={rowLabel}
            entries={rowSuppliers[supplierModalRowId] || []}
            knownSuppliers={knownSuppliers}
            onClose={() => setSupplierModalRowId(null)}
            onAdd={() => addRowSupplier(supplierModalRowId)}
            onUpdate={(entry, patch) => updateRowSupplier(supplierModalRowId, entry, patch)}
            onDelete={(entry) => deleteRowSupplier(supplierModalRowId, entry)}
          />
        );
      })()}

      {showKainininkasLink && linkModalRowId && (() => {
        const modalRow = current.rows.find(r => r.id === linkModalRowId);
        if (!modalRow) return null;
        const rowLabel = current.columns[0] ? modalRow.cells[current.columns[0].id] : "";
        return (
          <KainininkasLinkModal
            rowLabel={rowLabel}
            currentSource={modalRow.linkedRowId ? linkedSources[modalRow.linkedRowId] : null}
            onClose={() => setLinkModalRowId(null)}
            onSelect={(linkedRowId) => linkRowTo(linkModalRowId, linkedRowId)}
          />
        );
      })()}
    </div>
  );
}
